import { doc, serverTimestamp, setDoc } from 'firebase/firestore'
import { getMessaging, getToken, isSupported } from 'firebase/messaging'
import { app, db, logFirebaseError } from '../lib/firebase'
import { isIos, isStandalone, registerAdminServiceWorker } from '../utils/pwa'

/**
 * Offentlig web-push-nøgle (VAPID) fra Firebase Console → Projektindstillinger →
 * Cloud Messaging → Web Push certificates. Nøglen er offentlig og må stå i frontend —
 * den private del ligger kun hos Firebase.
 */
export const FCM_VAPID_KEY = 'BDgjxPCdcRHvBVh5pQydpEr1f-vHv1-DkSOUBK8bUhNHK0VzqDjs5nSersIv0hVPKbrLlSB1q1yMg8lEphNOQzM'

/** Status vist under Indstillinger → Notifikationer. */
export type PushStatus = 'active' | 'permission-missing' | 'unsupported' | 'ios-install' | 'checking'

export type PushResult = 'ok' | 'denied' | 'unsupported' | 'ios-not-installed' | 'no-service-worker' | 'failed'

export function notificationPermission(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported'
}

/** Kan denne enhed overhovedet modtage web-push? (uden at spørge om tilladelse) */
async function pushCapable(): Promise<boolean> {
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) return false
  if (!window.isSecureContext) return false
  return isSupported().catch(() => false)
}

/** Venter til service workeren er aktiv — getToken kræver en aktiv worker. */
async function activeRegistration(): Promise<ServiceWorkerRegistration | null> {
  const registration = await registerAdminServiceWorker()
  if (!registration) return null
  if (registration.active) return registration
  const worker = registration.installing ?? registration.waiting
  if (!worker) return registration
  await new Promise<void>((resolve) => {
    const timeout = window.setTimeout(resolve, 10_000)
    worker.addEventListener('statechange', () => {
      if (worker.state === 'activated') {
        window.clearTimeout(timeout)
        resolve()
      }
    })
  })
  return registration.active ? registration : null
}

/**
 * Henter FCM-tokenet via den eksisterende Firebase-app og vores service worker
 * (/sw.js, scope /admin/), og gemmer det under brugeren:
 * adminDevices/{uid}/tokens/{token}. Spørger ALDRIG om tilladelse selv.
 */
async function registerToken(uid: string): Promise<PushResult> {
  const registration = await activeRegistration()
  if (!registration) return 'no-service-worker'
  try {
    const token = await getToken(getMessaging(app), {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration: registration,
    })
    if (!token) return 'failed'
    await setDoc(
      doc(db, 'adminDevices', uid, 'tokens', token),
      { token, device: deviceName(), lastSeen: serverTimestamp() },
      { merge: true },
    )
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.info('[Push] FCM-token gemt for', uid)
    }
    return 'ok'
  } catch (error) {
    logFirebaseError('registerPushToken', error)
    return 'failed'
  }
}

/**
 * Beder om tilladelse og registrerer denne enhed til push.
 * Kaldes KUN når admin selv slår en påmindelse til — aldrig ved åbning af appen.
 */
export async function enablePush(uid: string): Promise<PushResult> {
  if (isIos() && !isStandalone()) return 'ios-not-installed'
  if (!(await pushCapable())) return 'unsupported'

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return 'denied'
  return registerToken(uid)
}

/**
 * Aktuel status uden at spørge om noget. Er tilladelsen allerede givet,
 * fornyes tokenet stille, så det altid er gyldigt i Firestore.
 */
export async function checkPushStatus(uid: string): Promise<PushStatus> {
  if (isIos() && !isStandalone()) return 'ios-install'
  if (!(await pushCapable())) return 'unsupported'
  if (Notification.permission !== 'granted') return 'permission-missing'
  const result = await registerToken(uid)
  if (result === 'ok') return 'active'
  return result === 'no-service-worker' ? 'unsupported' : 'permission-missing'
}

function deviceName(): string {
  const agent = navigator.userAgent
  if (/iphone/i.test(agent)) return 'iPhone'
  if (/ipad/i.test(agent)) return 'iPad'
  if (/android/i.test(agent)) return 'Android'
  if (/mac/i.test(agent)) return 'Mac'
  if (/windows/i.test(agent)) return 'Windows'
  return 'Browser'
}
